'use client';

import { useCallback } from 'react';

import type { AbstractIntlMessages } from 'next-intl';

import {
  I18nClientProvider,
  type MissingMessageReport,
} from '@kit/i18n/provider';
import { useMonitoring } from '@kit/monitoring/hooks';

/**
 * Binds next-intl's missing-message signal to monitoring.
 *
 * Lives here rather than in `@kit/i18n` for two reasons: the package stays
 * free of a monitoring dependency, and `useMonitoring` has to be called from
 * inside `MonitoringProvider`, which `RootProviders` renders above this.
 *
 * Why it matters: an unresolved key does not throw — it renders as its own key
 * text (`packages/ui/src/makerkit/trans.tsx`), and before this the only signal
 * was a `console.warn` in development. Narrowing the per-route message payload
 * is unsafe without this, because a namespace omitted by mistake would reach
 * customers silently.
 */
export function I18nProvider({
  locale,
  messages,
  children,
}: React.PropsWithChildren<{
  locale: string;
  messages?: AbstractIntlMessages;
}>) {
  const monitoring = useMonitoring();

  const onMissingMessage = useCallback(
    (report: MissingMessageReport) => {
      monitoring.captureEvent('i18n.missing_message', {
        i18n_path: report.path,
        i18n_namespace: report.namespace ?? null,
        i18n_key: report.key,
        i18n_locale: report.locale,
        i18n_code: report.code,
        // The route is what makes a report actionable: it names which
        // namespace set was too narrow.
        pathname: window.location.pathname,
      });
    },
    [monitoring],
  );

  return (
    <I18nClientProvider
      locale={locale}
      messages={messages}
      onMissingMessage={onMissingMessage}
    >
      {children}
    </I18nClientProvider>
  );
}
